import { useEffect, useRef, useState } from "react";

/**
 * Live input levels (0..1) per peerId for everyone in the room, via a WebAudio
 * analyser on each published audio track. Cheap: one small FFT per source, 8×/s.
 */
export function useAudioLevels(meeting, roster, active = true) {
  const [levels, setLevels] = useState({});
  const listRef = useRef(roster.list);
  listRef.current = roster.list;
  const nodesRef = useRef(new Map());
  const ctxRef = useRef(null);

  useEffect(() => {
    if (!meeting || !active) return undefined;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return undefined;
    const ctx = ctxRef.current || (ctxRef.current = new Ctx());
    const nodes = nodesRef.current;
    const id = setInterval(() => {
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const next = {};
      const seen = new Set();
      listRef.current.forEach((e) => {
        const track = e.rtk?.audioTrack;
        if (!track || !e.rtk?.audioEnabled || track.readyState !== "live") return;
        seen.add(e.peerId);
        let n = nodes.get(e.peerId);
        if (!n || n.track !== track) {
          try { n?.src.disconnect(); } catch (err) { /* noop */ }
          try {
            const src = ctx.createMediaStreamSource(new MediaStream([track]));
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            src.connect(analyser);
            n = { track, src, analyser, data: new Uint8Array(analyser.fftSize) };
            nodes.set(e.peerId, n);
          } catch (err) { return; }
        }
        n.analyser.getByteTimeDomainData(n.data);
        let sum = 0;
        for (let i = 0; i < n.data.length; i += 1) { const v = (n.data[i] - 128) / 128; sum += v * v; }
        next[e.peerId] = Math.min(1, Math.sqrt(sum / n.data.length) * 3.5);
      });
      nodes.forEach((n, k) => { if (!seen.has(k)) { try { n.src.disconnect(); } catch (err) { /* noop */ } nodes.delete(k); } });
      setLevels(next);
    }, 120);
    return () => {
      clearInterval(id);
      nodes.forEach((n) => { try { n.src.disconnect(); } catch (err) { /* noop */ } });
      nodes.clear();
      try { ctx.close(); } catch (err) { /* noop */ }
      if (ctxRef.current === ctx) ctxRef.current = null;
    };
  }, [meeting, active]);

  return levels;
}

export default useAudioLevels;
